import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRightIcon, CameraIcon, ClockIcon, MoonIcon } from "lucide-react";
import type { RouterOutputs } from "@bibacv/api-contracts";
import { orpc } from "@/lib/orpc";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { PageBody, PageHeader } from "@/components/layout/page-header";
import { AuthForm } from "@/routes/-components/auth-form";
import { RegisterStoreDialog } from "@/routes/stores/-components/register-store-dialog";
import { TrendSparkline } from "@/routes/stores/-components/trend-sparkline";

export const Route = createFileRoute("/")({
  component: Home,
});

function Home() {
  const { data: session, isPending } = useSession();

  if (isPending) return null;
  if (!session?.user) return <SignInScreen />;
  return <StoresPage />;
}

function SignInScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to BibaCV</h1>
          <p className="text-sm text-muted-foreground">
            Sign in to manage your stores and cameras.
          </p>
        </div>
        <AuthForm />
        {import.meta.env.DEV ? (
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Seeded demo account: <span data-numeric>demo@example.com</span> then{" "}
            <span data-numeric>password12345</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function StoresPage() {
  const stores = useQuery(orpc.store.list.queryOptions());
  const list = stores.data ?? [];

  return (
    <>
      <PageHeader title="Stores" actions={<RegisterStoreDialog />} />
      <PageBody>
        {stores.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-40 rounded-xl" />
            <Skeleton className="h-40 rounded-xl" />
            <Skeleton className="h-40 rounded-xl" />
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-xl border border-dashed px-6 py-14 text-center">
            <p className="text-sm font-medium">No stores yet</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Add a store to set up its cameras, floor plan and nightly run.
            </p>
            <div className="mt-5 flex justify-center">
              <RegisterStoreDialog />
            </div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {list.map((store) => (
              <StoreCard key={store.id} store={store} />
            ))}
          </div>
        )}
      </PageBody>
    </>
  );
}

type StoreSummary = RouterOutputs["store"]["list"][number];

function StoreCard({ store }: { store: StoreSummary }) {
  // The warehouse's latest night; quietly a dash when the warehouse is off
  // or the store's GPU machine is unreachable (parked between runs).
  const lastNight = useQuery({
    ...orpc.store.getTrends.queryOptions({ input: { slug: store.slug, days: 14 } }),
    retry: false,
  });
  const night = lastNight.data?.[0];

  const allCalibrated = store.cameraCount > 0 && store.calibratedCameraCount === store.cameraCount;
  const status = allCalibrated
    ? { label: "Ready", tone: "text-success" }
    : store.cameraCount === 0
      ? { label: "No cameras yet", tone: "text-muted-foreground" }
      : { label: "Needs calibration", tone: "text-warning" };

  return (
    <Link
      to="/stores/$storeSlug"
      params={{ storeSlug: store.slug }}
      className="focus-ring group flex flex-col gap-4 rounded-xl border p-5 transition-colors hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{store.name}</p>
          <p className="truncate text-xs text-muted-foreground" data-numeric>
            {store.slug}
          </p>
        </div>
        <ArrowRightIcon className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
      </div>

      <p className={cn("text-xs font-medium", status.tone)}>{status.label}</p>

      <dl className="mt-auto space-y-2 border-t pt-4 text-sm">
        <Row
          icon={CameraIcon}
          label="Cameras"
          value={`${store.calibratedCameraCount} of ${store.cameraCount} calibrated`}
        />
        <Row
          icon={ClockIcon}
          label="Runs nightly at"
          value={`${store.nightlyRunAt} ${store.timezone}`}
        />
        <Row
          icon={MoonIcon}
          label="Last night"
          value={night ? `${night.totalEntries} in · ${night.totalExits} out` : "—"}
          tone={night && night.status !== "ok" ? "text-warning" : undefined}
          visual={
            <TrendSparkline
              values={(lastNight.data ?? []).toReversed().map((n) => n.totalEntries)}
              className="h-4 w-16 shrink-0"
            />
          }
        />
      </dl>
    </Link>
  );
}

function Row({
  icon: Icon,
  label,
  value,
  tone,
  visual,
}: {
  icon: typeof CameraIcon;
  label: string;
  value: string;
  tone?: string;
  visual?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </dt>
      <dd className={cn("flex min-w-0 items-center gap-2 text-xs", tone)} data-numeric>
        {visual}
        <span className="truncate">{value}</span>
      </dd>
    </div>
  );
}
